import React, { useEffect, useMemo, useState } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import {
  Loader2, Container, Hammer, Trash2, RefreshCw, Bot, CheckCircle2, XCircle,
  MinusCircle, Zap,
} from 'lucide-react';

const STATUS_STYLE = {
  ok:        'text-buy border-buy',
  warnings:  'text-hold border-hold',
  failed:    'text-sell border-sell',
  stuck:     'text-sell border-sell',
  passed:    'text-buy border-buy',
  destroyed: 'text-[var(--txt-muted)] border-[var(--border-default)]',
};

/**
 * SandboxSection — Phase 51.L + 51.M unified UI.
 *
 * Left column: sandbox build list + create form (from a diff_id)
 * Right column: selected sandbox detail — worktree path, applied files,
 * compile status per file, and the autonomous test agent panel
 * (run history + "run test agent" trigger).
 */
export default function SandboxSection({ isProd }) {
  const [sandboxes, setSandboxes] = useState([]);
  const [diffs, setDiffs] = useState([]);
  const [diffId, setDiffId] = useState('');
  const [agentRuns, setAgentRuns] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [maxIter, setMaxIter] = useState(3);
  const [busy, setBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(null);

  const loadSandboxes = () =>
    api.get('/lab/sandbox/list?limit=50')
      .then(({ data }) => setSandboxes(data.builds || []))
      .catch(() => {});

  const loadAgentRuns = () =>
    api.get('/lab/test-agent/list?limit=50')
      .then(({ data }) => setAgentRuns(data.runs || []))
      .catch(() => {});

  useEffect(() => {
    loadSandboxes();
    loadAgentRuns();
    api.get('/lab/codediff/list?limit=50')
      .then(({ data }) => setDiffs(data.diffs || []))
      .catch(() => {});
  }, []);

  const createSandbox = async () => {
    if (!diffId || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/lab/sandbox/create/${diffId}`);
      toast.success(`Sandbox created · ${data.status}`);
      setSelectedId(data.id);
      loadSandboxes();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Create failed');
    } finally {
      setBusy(false);
    }
  };

  const recompile = async (sid) => {
    setActionBusy({ sid, kind: 'recompile' });
    try {
      await api.post(`/lab/sandbox/${sid}/recompile`);
      toast.success('Recompiled');
      loadSandboxes();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Recompile failed');
    } finally {
      setActionBusy(null);
    }
  };

  const destroySandbox = async (sid) => {
    setActionBusy({ sid, kind: 'destroy' });
    try {
      await api.post(`/lab/sandbox/${sid}/destroy`);
      toast.success('Sandbox destroyed');
      loadSandboxes();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Destroy failed');
    } finally {
      setActionBusy(null);
    }
  };

  const runAgent = async (sid) => {
    setActionBusy({ sid, kind: 'agent' });
    try {
      const { data } = await api.post(`/lab/test-agent/run/${sid}`, { max_iterations: maxIter });
      toast.success(
        `Agent finished · ${data.iterations} iter · ${data.final_status}`,
      );
      loadSandboxes();
      loadAgentRuns();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Agent run failed');
    } finally {
      setActionBusy(null);
    }
  };

  const selected = useMemo(
    () => sandboxes.find((s) => s.id === selectedId),
    [sandboxes, selectedId],
  );

  const selectedRuns = useMemo(
    () => agentRuns.filter((r) => r.sandbox_id === selectedId),
    [agentRuns, selectedId],
  );

  return (
    <div data-testid="lab-sandbox" className="space-y-3">
      {/* CREATE FORM */}
      <div className="border border-cyan-sys p-3">
        <div className="text-[10px] tracking-[0.25em] uppercase text-cyan-sys mb-2">
          // isolated build sandbox
        </div>
        <div className="flex flex-col md:flex-row md:items-center gap-2">
          <select
            data-testid="lab-sandbox-diff-select"
            value={diffId}
            onChange={(e) => setDiffId(e.target.value)}
            disabled={!isProd || busy}
            className="flex-1 bg-transparent border border-[var(--border-default)] px-2 py-2 text-[11px] font-mono focus:border-cyan-sys outline-none"
          >
            <option value="">— select code diff to sandbox —</option>
            {diffs.map((d) => (
              <option key={d.id} value={d.id}>
                {d.id.slice(0, 8)} · {(d.summary || 'untitled').slice(0, 60)} · {(d.diffs || []).length} file(s)
              </option>
            ))}
          </select>
          <button
            data-testid="lab-sandbox-create"
            onClick={createSandbox}
            disabled={!isProd || busy || !diffId}
            className="flex items-center justify-center gap-1.5 border border-cyan-sys text-cyan-sys px-3 py-2 text-[10px] uppercase tracking-[0.2em] hover:bg-cyan-sys hover:text-black disabled:opacity-30 disabled:cursor-not-allowed"
          >
            {busy ? <Loader2 size={11} className="animate-spin" /> : <Container size={11} />}
            {busy ? 'building...' : 'create sandbox'}
          </button>
        </div>
        <div className="text-[10px] text-[var(--txt-secondary)] mt-2">
          {isProd
            ? 'Creates a git worktree under /tmp/atde_sandbox, applies the diff, and runs static compile checks (python -m py_compile / node --check). Isolated Mongo DB per sandbox. No live-code side effects.'
            : '⚠ Sandbox writes are production-only. List below is read-only here.'}
        </div>
      </div>

      {/* LIST + DETAIL */}
      <div className="grid md:grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <div className="text-[10px] tracking-[0.25em] uppercase text-[var(--txt-secondary)] mb-1">
            {sandboxes.length} sandbox build(s)
          </div>
          {sandboxes.length === 0 ? (
            <div className="border border-[var(--border-default)] p-4 text-[11px] text-[var(--txt-secondary)]">
              No sandboxes yet. Pick a code diff above and build one.
            </div>
          ) : (
            sandboxes.map((s) => (
              <button
                key={s.id}
                data-testid={`lab-sandbox-${s.id}`}
                onClick={() => setSelectedId(s.id)}
                className={`w-full text-left border p-2.5 transition-colors ${
                  s.id === selectedId
                    ? 'border-cyan-sys bg-[var(--bg-panel-lighter)]'
                    : 'border-[var(--border-default)] hover:border-cyan-sys'
                }`}
              >
                <div className="flex items-center justify-between gap-2 mb-0.5">
                  <span className="text-[11px] text-[var(--txt-primary)] truncate font-mono">
                    sb_{s.id} · diff {String(s.diff_id || '').slice(0, 8)}
                  </span>
                  <span className={`text-[9px] tracking-[0.2em] uppercase px-1.5 py-0.5 border ${STATUS_STYLE[s.status] || 'text-[var(--txt-muted)] border-[var(--border-default)]'}`}>
                    {s.status}
                  </span>
                </div>
                <div className="text-[9px] text-[var(--txt-secondary)] truncate">
                  {(s.created_at || '').slice(0, 19).replace('T', ' ')}Z ·
                  {' '}{(s.applied_files || []).length} file(s) ·
                  {' '}{(s.compile_status || []).filter((c) => c.ok === false).length} error(s)
                </div>
              </button>
            ))
          )}
        </div>

        {/* DETAIL */}
        <div className="border border-[var(--border-default)] p-3 min-h-[420px]">
          {!selected ? (
            <div className="text-[11px] text-[var(--txt-secondary)]">Select a sandbox to inspect.</div>
          ) : (
            <div className="space-y-3 text-[11px]">
              <div className="flex items-center justify-between">
                <div className="text-[9px] tracking-[0.25em] uppercase text-cyan-sys">// sandbox</div>
                <span className={`text-[9px] tracking-[0.2em] uppercase px-1.5 py-0.5 border ${STATUS_STYLE[selected.status] || ''}`}>
                  {selected.status}
                </span>
              </div>

              <KV k="sandbox_id" v={selected.id} />
              <KV k="worktree"   v={selected.worktree_path} />
              <KV k="isolated_db" v={selected.isolated_db} />
              <KV k="diff_id"    v={selected.diff_id} />
              {selected.used_fallback && (
                <div className="border border-hold text-hold p-1.5 text-[10px] font-mono">
                  ⚠ git worktree unavailable — shallow-copy fallback used.
                </div>
              )}

              <div>
                <div className="text-[9px] tracking-[0.25em] uppercase text-cyan-sys mb-1">// compile status</div>
                <ul className="space-y-0.5">
                  {(selected.compile_status || []).map((c, i) => (
                    <li key={i} className="text-[10px] font-mono flex items-start gap-1.5">
                      {c.ok === true  && <CheckCircle2 size={11} className="text-buy mt-0.5 shrink-0" />}
                      {c.ok === false && <XCircle size={11} className="text-sell mt-0.5 shrink-0" />}
                      {c.ok === null  && <MinusCircle size={11} className="text-hold mt-0.5 shrink-0" />}
                      <div className="min-w-0">
                        <div className="text-[var(--txt-primary)] truncate">
                          {c.file} <span className="text-[var(--txt-muted)]">· {c.tool}</span>
                        </div>
                        {c.error && (
                          <pre className="text-sell text-[9px] whitespace-pre-wrap break-all mt-0.5">
                            {c.error.length > 300 ? c.error.slice(0, 300) + '…' : c.error}
                          </pre>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              {/* AGENT PANEL */}
              {selected.status !== 'destroyed' && (
                <div className="pt-2 border-t border-[var(--border-default)] space-y-2">
                  <div className="text-[9px] tracking-[0.25em] uppercase text-cyan-sys flex items-center gap-1.5">
                    <Bot size={11} /> autonomous test agent
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-[10px] text-[var(--txt-secondary)] uppercase tracking-[0.15em]">
                      max iterations
                    </label>
                    <input
                      type="number" min={1} max={6} value={maxIter}
                      onChange={(e) => setMaxIter(parseInt(e.target.value) || 3)}
                      disabled={!isProd}
                      className="w-16 bg-transparent border border-[var(--border-default)] px-1.5 py-0.5 text-[10px] font-mono focus:border-cyan-sys outline-none"
                    />
                    <button
                      data-testid="lab-sandbox-run-agent"
                      onClick={() => runAgent(selected.id)}
                      disabled={!isProd || actionBusy?.sid === selected.id || selected.status === 'ok'}
                      className="flex items-center gap-1 border border-buy text-buy px-2 py-1 text-[10px] uppercase tracking-[0.2em] hover:bg-buy hover:text-black disabled:opacity-30 disabled:cursor-not-allowed"
                      title={selected.status === 'ok' ? 'nothing to fix — already passing' : ''}
                    >
                      {actionBusy?.sid === selected.id && actionBusy.kind === 'agent'
                        ? <Loader2 size={11} className="animate-spin" />
                        : <Zap size={11} />}
                      run agent
                    </button>
                    <button
                      data-testid="lab-sandbox-recompile"
                      onClick={() => recompile(selected.id)}
                      disabled={!isProd || actionBusy?.sid === selected.id}
                      className="flex items-center gap-1 border border-cyan-sys text-cyan-sys px-2 py-1 text-[10px] uppercase tracking-[0.2em] hover:bg-cyan-sys hover:text-black disabled:opacity-30"
                    >
                      {actionBusy?.sid === selected.id && actionBusy.kind === 'recompile'
                        ? <Loader2 size={11} className="animate-spin" />
                        : <RefreshCw size={11} />}
                      recompile
                    </button>
                    <div className="flex-1" />
                    <button
                      data-testid="lab-sandbox-destroy"
                      onClick={() => destroySandbox(selected.id)}
                      disabled={!isProd || actionBusy?.sid === selected.id}
                      className="flex items-center gap-1 border border-sell text-sell px-2 py-1 text-[10px] uppercase tracking-[0.2em] hover:bg-sell hover:text-black disabled:opacity-30"
                    >
                      {actionBusy?.sid === selected.id && actionBusy.kind === 'destroy'
                        ? <Loader2 size={11} className="animate-spin" />
                        : <Trash2 size={11} />}
                      destroy
                    </button>
                  </div>

                  {selectedRuns.length > 0 && (
                    <div className="space-y-1.5">
                      <div className="text-[9px] tracking-[0.25em] uppercase text-[var(--txt-secondary)]">
                        {selectedRuns.length} agent run(s) on this sandbox
                      </div>
                      {selectedRuns.slice(0, 5).map((r) => (
                        <details
                          key={r.id}
                          data-testid={`lab-sandbox-agent-run-${r.id}`}
                          className="border border-[var(--border-default)] text-[10px]"
                        >
                          <summary className="cursor-pointer px-2 py-1 flex items-center justify-between hover:bg-[var(--bg-panel-lighter)]">
                            <span className="font-mono">
                              {r.id.slice(0, 8)} · {r.iterations} iter · {r.attempts.length} attempt(s)
                            </span>
                            <span className={`text-[9px] tracking-[0.15em] uppercase px-1.5 py-0.5 border ${STATUS_STYLE[r.final_status] || ''}`}>
                              {r.initial_status} → {r.final_status}
                            </span>
                          </summary>
                          <ul className="border-t border-[var(--border-default)] px-2 py-1.5 space-y-1">
                            {r.attempts.map((a, i) => (
                              <li key={i} className="font-mono">
                                <div className="text-[var(--txt-primary)]">
                                  <span className="text-cyan-sys">it{a.iteration}</span>
                                  {' · '}
                                  <span className={a.patched ? 'text-buy' : 'text-hold'}>
                                    {a.action}
                                  </span>
                                  {' · '}
                                  {a.file}
                                </div>
                                {a.reasoning && (
                                  <div className="text-[var(--txt-secondary)] text-[9px] ml-4">
                                    {a.reasoning}
                                  </div>
                                )}
                              </li>
                            ))}
                          </ul>
                        </details>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function KV({ k, v }) {
  return (
    <div className="flex items-baseline gap-2 text-[10px] font-mono">
      <span className="text-[var(--txt-secondary)] uppercase tracking-[0.15em] min-w-[90px]">
        {k}
      </span>
      <span className="text-[var(--txt-primary)] break-all">{v || '—'}</span>
    </div>
  );
}
